/**
 * useFlowEdges — Kanten aus gemessenen Knoten-Rechtecken.
 *
 * Von Startseite und Architektur geteilt: der Hook misst nach dem Layout die
 * `[data-flow-node]`-Rechtecke im Canvas und liefert 1-px-Bézier-Pfade für ein
 * SVG-Overlay (`FlowEdges`). Kein Canvas-Element, keine Layout-Bibliothek,
 * keine neue Dependency.
 *
 * `selectLiveEdges` ist die zweite Hälfte davon: die gezeichneten Kanten werden
 * aus DERSELBEN Quelle gefiltert wie die Bahnkopfzahl (`pipeline.json`), damit
 * beide nicht auseinanderlaufen können — „3 von 9 lesen diesen Stand" heißt
 * dann auch 3 gezeichnete Kanten.
 *
 * Bekannte Kopplung (design-hig.md, Risiko 2): Messen koppelt Rendering an
 * Layout. Deshalb `useLayoutEffect` + `ResizeObserver` + eine Nachmessung, wenn
 * die Schriften geladen sind — sonst steht nach einem Font-Swap ein Frame lang
 * die falsche Kurve.
 */
import { useCallback, useEffect, useLayoutEffect, useRef, useState, type RefObject } from "react";
import { ALL, type FlowState, type PipelineEdge, type ReleaseKey } from "@/types/pipeline";
import { isStackedPair, stackedEdgePath, type EdgeBox } from "./stackedEdges";

export interface FlowEdgeSpec {
  from: string;
  to: string;
  /** true = aus Freitext heuristisch aufgelöst → wird gestrichelt gezeichnet. */
  derived?: boolean;
}

export interface FlowEdgePath {
  key: string;
  from: string;
  to: string;
  derived: boolean;
  /** `d`-Attribut des Bézier — bei bahninternen Kanten ein gerader Schaft. */
  d: string;
  /** Beide Enden in derselben Bahn → eigene, gerichtete Führung. */
  stacked: boolean;
  /** Pfeilspitze der bahninternen Kante; sonst `null` (dort steht der Punkt). */
  arrow: string | null;
  /** Endpunkt (Zielknoten) für den Punkt am Kantenende. */
  x: number;
  y: number;
}

export interface FlowEdgesGeometry {
  paths: FlowEdgePath[];
  width: number;
  height: number;
  /** Zahl der gezeichneten Kanten. */
  total: number;
  /** Davon heuristisch abgeleitet. */
  derived: number;
  /** Davon bahnintern (beide Enden in derselben Bahn). */
  stacked: number;
}

const EMPTY: FlowEdgesGeometry = { paths: [], width: 0, height: 0, total: 0, derived: 0, stacked: 0 };

export interface UseFlowEdgesOptions {
  canvasRef: RefObject<HTMLElement | null>;
  edges: FlowEdgeSpec[];
  /** Auswahl der messbaren Knoten. Default `[data-flow-node]`. */
  selector?: string;
  /** false = nichts messen und nichts zeichnen (z. B. Zoomstufe „Übersicht"). */
  enabled?: boolean;
}

export function useFlowEdges({
  canvasRef,
  edges,
  selector = "[data-flow-node]",
  enabled = true,
}: UseFlowEdgesOptions): FlowEdgesGeometry {
  const [geometry, setGeometry] = useState<FlowEdgesGeometry>(EMPTY);
  const lastRef = useRef<string>("");
  // Kanten kommen meist als frisch gebautes Array — an der Identität darf kein
  // Effekt hängen, sonst misst der Hook in jedem Render neu.
  const key = edges.map((e) => `${e.from}>${e.to}${e.derived ? "~" : ""}`).join("|");
  const edgesRef = useRef(edges);
  edgesRef.current = edges;

  const measure = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !enabled) {
      if (lastRef.current !== "") {
        lastRef.current = "";
        setGeometry(EMPTY);
      }
      return;
    }
    const base = canvas.getBoundingClientRect();
    if (!base.width) return;

    const rect = (id: string): { el: Element; box: EdgeBox } | null => {
      const el = canvas.querySelector(`${selector}[data-flow-node="${cssEscape(id)}"]`);
      if (!el) return null;
      const r = el.getBoundingClientRect();
      return {
        el,
        box: {
          left: r.left - base.left,
          right: r.right - base.left,
          top: r.top - base.top,
          bottom: r.bottom - base.top,
          cx: r.left - base.left + r.width / 2,
          cy: r.top - base.top + r.height / 2,
        },
      };
    };

    const paths: FlowEdgePath[] = [];
    let derived = 0;
    let stacked = 0;
    for (const e of edgesRef.current) {
      const a = rect(e.from);
      const b = rect(e.to);
      if (!a || !b) continue;
      if (e.derived) derived += 1;
      const key = `${e.from}>${e.to}`;

      // Bahnintern: die Bézier von Rand zu Rand hätte hier keine Länge und
      // keine Richtung. Stattdessen Schaft + Pfeilspitze (stackedEdges.ts).
      if (isStackedPair(a.el, b.el)) {
        const p = stackedEdgePath(a.box, b.box);
        stacked += 1;
        paths.push({
          key,
          from: e.from,
          to: e.to,
          derived: !!e.derived,
          stacked: true,
          d: p.d,
          arrow: p.arrow,
          x: p.x,
          y: p.y,
        });
        continue;
      }

      const x1 = a.box.right + 1;
      const y1 = a.box.cy;
      const x2 = b.box.left - 1;
      const y2 = b.box.cy;
      const dx = Math.max(14, (x2 - x1) / 2);
      paths.push({
        key,
        from: e.from,
        to: e.to,
        derived: !!e.derived,
        stacked: false,
        arrow: null,
        d:
          `M${x1.toFixed(1)} ${y1.toFixed(1)} ` +
          `C${(x1 + dx).toFixed(1)} ${y1.toFixed(1)}, ${(x2 - dx).toFixed(1)} ${y2.toFixed(1)}, ` +
          `${x2.toFixed(1)} ${y2.toFixed(1)}`,
        x: x2,
        y: y2,
      });
    }

    const next: FlowEdgesGeometry = {
      paths,
      width: base.width,
      height: base.height,
      total: paths.length,
      derived,
      stacked,
    };
    // Ohne diesen Vergleich würde jeder ResizeObserver-Tick einen Render
    // auslösen, der wieder einen Tick auslösen kann.
    const stamp = JSON.stringify(next);
    if (stamp === lastRef.current) return;
    lastRef.current = stamp;
    setGeometry(next);
  }, [canvasRef, enabled, selector]);

  // Zwei Bahnen, mit Absicht:
  //  - `useLayoutEffect` misst vor dem Paint, sobald sich etwas ändert.
  //  - `useEffect` misst noch einmal nach dem Commit. Beim ERSTEN Commit läuft
  //    der Layout-Effekt dieses Kindes, bevor React die `ref` des Canvas am
  //    Elternelement gesetzt hat — ohne die zweite Bahn bliebe das Overlay
  //    dauerhaft leer.
  // Der Stempel-Vergleich in `measure` macht die Doppelmessung folgenlos.
  useLayoutEffect(() => {
    measure();
  });

  useEffect(() => {
    measure();
  });

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !enabled) return;

    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", measure);
      return () => window.removeEventListener("resize", measure);
    }
    const ro = new ResizeObserver(() => measure());
    ro.observe(canvas);
    // Auch die Knoten selbst beobachten: ihre Höhe ändert sich beim
    // Zoomwechsel, ohne dass der Canvas seine Breite ändert.
    canvas.querySelectorAll(selector).forEach((el) => ro.observe(el));
    window.addEventListener("resize", measure);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", measure);
    };
    // `key` hält den Effekt an den Kanten-Inhalten, nicht an der Array-Identität.
  }, [measure, canvasRef, enabled, selector, key]);

  useEffect(() => {
    const fonts = (document as Document & { fonts?: FontFaceSet }).fonts;
    if (!fonts?.ready) return;
    let cancelled = false;
    void fonts.ready.then(() => {
      if (!cancelled) measure();
    });
    return () => {
      cancelled = true;
    };
  }, [measure]);

  return geometry;
}

/**
 * Die Kanten, die für den gewählten Stand wirklich gelten — dieselbe Quelle wie
 * die Bahnkopfzahl.
 *
 * 1. Der Stand muss in `edge.releases` stehen (bei „Alle" gilt jede Kante).
 * 2. Kanten in Knoten, die dieser Stand gar nicht erreicht (`ghost`/`skipped`),
 *    werden nicht gezeichnet — bei 202710 bleibt rechts der Knowledge-Bahn
 *    deshalb alles unverbunden, statt eine Verarbeitung zu behaupten.
 */
export function selectLiveEdges(
  edges: PipelineEdge[],
  release: ReleaseKey,
  stateOf?: (nodeId: string) => FlowState | undefined,
): PipelineEdge[] {
  return edges.filter((e) => {
    if (release !== ALL && Array.isArray(e.releases) && !e.releases.includes(release)) return false;
    if (stateOf) {
      const target = stateOf(e.to);
      if (target === "ghost" || target === "skipped") return false;
    }
    return true;
  });
}

/**
 * Sichtbarer Zähler „n Kanten · m abgeleitet" (aus dense übernommen), erweitert
 * um „· k bahnintern". Die bahninternen Kanten sind die zwei, die niemand sah;
 * ihre ZAHL steht hier, ihre NAMEN stehen im `aria-label` der beteiligten
 * Knoten (`useFlowRelations`) — der Zähler bliebe sonst eine Behauptung ohne
 * Beteiligte.
 */
export function edgeCountLabel(geometry: { total: number; derived: number; stacked?: number }): string {
  const parts = [`${geometry.total} ${geometry.total === 1 ? "Kante" : "Kanten"}`];
  if (geometry.derived > 0) parts.push(`${geometry.derived} abgeleitet`);
  if (geometry.stacked) parts.push(`${geometry.stacked} bahnintern`);
  return parts.join(" · ");
}

function cssEscape(value: string): string {
  const g = globalThis as { CSS?: { escape?: (v: string) => string } };
  if (typeof g.CSS?.escape === "function") return g.CSS.escape(value);
  return value.replace(/["\\]/g, "\\$&");
}
